import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Pencil } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import {
  useSetActiveSubscription,
  useSubscriptionStats,
  useSubscriptions,
} from '../features/subscriptions/api';
import { RenameSubscriptionModal } from '../features/subscriptions/RenameSubscriptionModal';
import { clipLabel, resolveSubscriptionLabel } from '../features/subscriptions/labels';
import { Button } from '../shared/ui/Button';
import { Skeleton } from '../shared/ui/Skeleton';

export function SubscriptionCardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscriptionId } = useParams({ strict: false }) as { subscriptionId?: string };
  const search = useSearch({ strict: false }) as { rename?: boolean };
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const subsQuery = useSubscriptions(isAuthenticated);
  const setActive = useSetActiveSubscription();
  const statsQuery = useSubscriptionStats(subscriptionId, isAuthenticated && Boolean(subscriptionId));

  const items = subsQuery.data?.items ?? [];
  const index = items.findIndex((s) => s.serviceSubscriptionId === subscriptionId);
  const sub = index >= 0 ? items[index] : undefined;
  const label = sub
    ? resolveSubscriptionLabel(sub.serviceLabel, index, (n) => t('subscriptions.defaultName', { n }))
    : '';

  if (authLoading || subsQuery.isLoading) {
    return (
      <section className="page space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-28 w-full" />
      </section>
    );
  }

  if (!isAuthenticated) {
    return (
      <section className="page">
        <p>{t('auth.notAuthenticated')}</p>
        <button type="button" className="mt-3 text-brand underline" onClick={() => { login(); }}>
          {t('auth.login')}
        </button>
      </section>
    );
  }

  if (!sub) {
    return (
      <section className="page">
        <p className="text-fg-muted">{t('subscriptions.empty')}</p>
        <Button variant="link" className="mt-3" onClick={() => { void navigate({ to: '/mailboxes' }); }}>
          {t('subscriptions.selectTitle')}
        </Button>
      </section>
    );
  }

  const stats = statsQuery.data;
  const backTo = items.length > 1 ? '/mailboxes' : '/';
  const statusKey = `subscriptions.status.${sub.status}`;
  const statusText = t(statusKey) === statusKey ? sub.status : t(statusKey);

  return (
    <section className="mx-auto max-w-3xl">
      <button
        type="button"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg"
        onClick={() => { void navigate({ to: backTo }); }}
      >
        <ArrowLeft className="h-4 w-4" />
        {t('app.back')}
      </button>

      <header className="mb-6">
        <h1 className="text-2xl font-bold text-fg" title={label}>{clipLabel(label)}</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {sub.servicePlan ? `${sub.servicePlan} · ` : ''}
          {statusText}
        </p>
        <p className="mt-2 text-sm text-fg-muted">{t('subscriptions.changePlanWithPartner')}</p>
      </header>

      <div className="sub-stat-grid mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat
          value={stats ? `${stats.mailboxCount} ${t('subscriptions.statOf')} ${stats.mailboxLimit}` : '—'}
          label={t('subscriptions.statMailboxes')}
        />
        <Stat
          value={stats ? `${formatMb(stats.usedMb)} ${t('subscriptions.statMb')}` : '—'}
          label={t('subscriptions.statUsed')}
        />
        <Stat
          value={stats ? `${formatMb(stats.freeMb)} ${t('subscriptions.statMb')}` : '—'}
          label={t('subscriptions.statFree', { limit: formatMb(stats?.limitMb ?? 0) })}
        />
        <Stat
          value={stats ? String(stats.forwardedCount) : '—'}
          label={t('subscriptions.statForwarded')}
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          loading={setActive.isPending}
          onClick={() => {
            setActive.mutate(sub.serviceSubscriptionId, { onSuccess: () => { void navigate({ to: '/' }); } });
          }}
        >
          {t('subscriptions.openMailboxes')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            void navigate({
              to: '/subscriptions/$subscriptionId',
              params: { subscriptionId: sub.serviceSubscriptionId },
              search: { rename: true },
            });
          }}
        >
          <Pencil className="h-4 w-4" />
          {t('subscriptions.renameTitle')}
        </Button>
      </div>

      <RenameSubscriptionModal
        subscriptionId={sub.serviceSubscriptionId}
        initialLabel={label}
        open={search.rename === true}
        onOpenChange={(next) => {
          void navigate({
            to: '/subscriptions/$subscriptionId',
            params: { subscriptionId: sub.serviceSubscriptionId },
            search: next ? { rename: true } : {},
          });
        }}
      />
    </section>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-xl bg-page px-4 py-3.5">
      <b className="block text-lg font-bold tracking-tight text-fg">{value}</b>
      <span className="mt-0.5 block text-xs text-fg-muted">{label}</span>
    </div>
  );
}

function formatMb(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
